import React from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { VENDOR_FIELDS, FIELD_GROUPS } from "@/lib/fields";
import { cellStatus, normalizeValue, rowIssues } from "@/lib/validation";
import { cn } from "@/lib/utils";

export const VendorCards = ({ rows, onCellChange, onDeleteRow }) => (
  <Accordion
    type="multiple"
    defaultValue={rows.length ? [rows[0]._rid] : []}
    className="space-y-2"
    data-testid="vendor-mobile-list"
  >
    {rows.map((row, idx) => {
      const issues = rowIssues(row);
      return (
        <AccordionItem
          key={row._rid}
          value={row._rid}
          className="overflow-hidden rounded-xl border border-border bg-card"
          data-testid="vendor-mobile-card"
        >
          <AccordionTrigger className="px-3 py-3 text-left hover:no-underline">
            <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
              <span className="vde-code shrink-0 text-[11px] text-muted-foreground">
                {idx + 1}
              </span>
              <span className="truncate text-sm font-semibold">
                {row.company_name || "Unnamed vendor"}
              </span>
              {issues.invalid ? (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-danger-surface px-2 py-0.5 text-[10px] font-semibold text-danger-text">
                  <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" /> Invalid
                </span>
              ) : null}
            </div>
          </AccordionTrigger>
          <AccordionContent className="border-t border-border bg-secondary/50 px-3 py-3">
            {FIELD_GROUPS.map((group) => {
              const fields = VENDOR_FIELDS.filter((f) => f.group === group);
              if (!fields.length) return null;
              return (
                <div key={group} className="mb-4 last:mb-0">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group}
                  </p>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {fields.map((f) => {
                      const value = row[f.key] ?? "";
                      const st = cellStatus(f.key, value);
                      const isBad = st.level === "error";
                      const isWarn = st.level === "warn";
                      const isNeeds = st.level === "needs";
                      const common = {
                        value,
                        onChange: (e) => onCellChange(row._rid, f.key, e.target.value),
                        "data-testid": `m-input-${f.key}-${idx}`,
                        className: cn(
                          "bg-card text-[13px]",
                          f.mono && "vde-code",
                          isBad && "border-danger-border bg-danger-surface",
                          isWarn && "border-warning-border bg-warning-surface",
                          isNeeds && "border-warning-border bg-warning-surface"
                        ),
                      };
                      return (
                        <div key={f.key} className={cn(f.long && "sm:col-span-2")}>
                          <Label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                            {f.label}
                          </Label>
                          {f.long ? (
                            <Textarea rows={3} {...common} />
                          ) : (
                            <Input {...common} />
                          )}
                          {st.message ? (
                            <p
                              className={cn(
                                "mt-1 flex items-start gap-1 text-[10px]",
                                isBad ? "text-danger-text" : "text-warning-text"
                              )}
                            >
                              <AlertTriangle className="mt-px h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                              {st.message}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => onDeleteRow(row._rid)}
              data-testid={`m-delete-row-${idx}`}
              className="mt-1 inline-flex h-10 items-center gap-1.5 rounded-lg border border-danger-border bg-card px-3 text-xs font-medium text-danger-text transition-colors hover:bg-danger-surface"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Remove this vendor
            </button>
          </AccordionContent>
        </AccordionItem>
      );
    })}
  </Accordion>
);

export default VendorCards;

// normalizeValue is applied by the parent; exported here for clarity of intent
export { normalizeValue };
